import {FC, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Alert, App, Card, Checkbox, ConfigProvider, DatePicker, Empty, InputNumber, Select, Space, Spin, Table, TimePicker, Typography, theme} from "antd";
import {LockOutlined, TeamOutlined} from "@ant-design/icons";
import dayjs, {Dayjs} from "dayjs";
import AxiosServices from "../../Services/axiosServices";
import {IMatch} from "../../Interfaces/IMatch";
import {matchAsUtcDate} from "../../Services/datetime";
import {useAuth} from "../../config/AuthContext";
import {useGlobal} from "../../config/GlobalContext";

const ACCENT = '#203923';
const {Paragraph} = Typography;

// Per-row debounce window for auto-save: keeps InputNumber typing
// fluid while still feeling immediate for picker-based fields.
const AUTOSAVE_DELAY_MS = 600;

type TeamOpt = {idt: number; nom: string; nomLong: string};
type StadeOpt = {idt: number; nom: string; ville: string};

type RowDraft = {
    num: number | null;
    dte: Dayjs | null;
    hre: Dayjs | null;
    teamIdtDom: number | null;
    teamIdtExt: number | null;
    // 3rd/4th place playoff flag: when on, the backend auto-derives
    // both sides from the semi-final losers (no admin pick needed).
    isThirdPlace: boolean;
    stadeIdt: number | null;
};

const buildDraft = (m: IMatch): RowDraft => ({
    num: m.Num ?? null,
    dte: m.Dte ? dayjs(m.Dte, ['YYYY-MM-DD', 'DD/MM/YYYY']) : null,
    hre: m.Hre ? dayjs(m.Hre, ['HH:mm:ss', 'HH:mm']) : null,
    teamIdtDom: m.team_idt_Dom ? Number(m.team_idt_Dom) : null,
    teamIdtExt: m.team_idt_Ext ? Number(m.team_idt_Ext) : null,
    isThirdPlace: Number(m.Is_Third_Place ?? 0) === 1,
    stadeIdt: m.Stade_Idt ? Number(m.Stade_Idt) : null,
});

const sameDraft = (a: RowDraft, b: RowDraft): boolean => {
    if (a.num !== b.num) return false;
    if ((a.dte?.format('YYYY-MM-DD') ?? '') !== (b.dte?.format('YYYY-MM-DD') ?? '')) return false;
    if ((a.hre?.format('HH:mm:ss') ?? '') !== (b.hre?.format('HH:mm:ss') ?? '')) return false;
    if (a.teamIdtDom !== b.teamIdtDom) return false;
    if (a.teamIdtExt !== b.teamIdtExt) return false;
    if (a.isThirdPlace !== b.isThirdPlace) return false;
    if (a.stadeIdt !== b.stadeIdt) return false;
    return true;
};

const AdminMatches: FC = () => {
    const {isAdmin, isLoggedIn} = useAuth();
    const {currentCmpEdt} = useGlobal();
    const {message} = App.useApp();

    const [matches, setMatches] = useState<IMatch[]>([]);
    const [teams, setTeams] = useState<TeamOpt[]>([]);
    const [stades, setStades] = useState<StadeOpt[]>([]);
    const [loading, setLoading] = useState(false);
    const [drafts, setDrafts] = useState<Record<number, RowDraft>>({});
    const [savingIdts, setSavingIdts] = useState<Set<number>>(new Set());
    // Refs read by the debounced save closure — without them we'd
    // capture stale state from the moment the timer was scheduled.
    const matchesRef = useRef(matches);
    const draftsRef = useRef(drafts);
    useEffect(() => { matchesRef.current = matches; }, [matches]);
    useEffect(() => { draftsRef.current = drafts; }, [drafts]);
    const saveTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

    const refresh = useCallback(async () => {
        if (!currentCmpEdt) return;
        setLoading(true);
        try {
            const list: IMatch[] = await new Promise((resolve, reject) =>
                AxiosServices.getMatches(resolve, currentCmpEdt).catch(reject),
            );
            setMatches(list);

            // Reseed only matches we don't already have a draft for.
            // Preserves in-flight edits in other rows while a sibling
            // row's auto-save is round-tripping.
            setDrafts(prev => {
                const seed: Record<number, RowDraft> = {};
                list.forEach(m => { seed[m.Idt] = prev[m.Idt] ?? buildDraft(m); });
                return seed;
            });

            const teamRows = await AxiosServices.getCmpEdtTeams(currentCmpEdt).catch(() => [] as TeamOpt[]);
            setTeams(teamRows);

            const stadeRows = await AxiosServices.getStades(currentCmpEdt).catch(() => [] as StadeOpt[]);
            setStades(stadeRows);
        } catch {
            message.error("Impossible de charger les matches.");
        } finally {
            setLoading(false);
        }
    }, [currentCmpEdt, message]);

    useEffect(() => {
        if (!isAdmin || !currentCmpEdt) return;
        refresh();
    }, [isAdmin, currentCmpEdt, refresh]);

    const baseTeamOptions = useMemo(
        () => teams
            .slice()
            .sort((a, b) => a.nom.localeCompare(b.nom))
            .map(t => ({value: t.idt, label: t.nom})),
        [teams],
    );

    // Always offer the full pool of teams participating in the competition.
    // Falls back to a synthetic option for the team currently saved on the
    // match so the Select can render its label even if the pool doesn't
    // list it (e.g. team removed from teamcmpedt after the pick).
    const teamOptionsForSide = useCallback((m: IMatch, side: 'D' | 'E') => {
        const opts = [...baseTeamOptions];
        const curIdt = side === 'D'
            ? (m.team_idt_Dom ? Number(m.team_idt_Dom) : null)
            : (m.team_idt_Ext ? Number(m.team_idt_Ext) : null);
        const curName = side === 'D' ? m.Nom_Dom : m.Nom_Ext;
        if (curIdt && !opts.some(o => o.value === curIdt)) {
            opts.push({value: curIdt, label: curName ?? `#${curIdt}`});
        }
        return opts;
    }, [baseTeamOptions]);

    const baseStadeOptions = useMemo(
        () => stades
            .slice()
            .sort((a, b) => a.nom.localeCompare(b.nom))
            .map(s => ({value: s.idt, label: `${s.nom} (${s.ville})`})),
        [stades],
    );

    // Match may already reference a stade not in the loaded list
    // (rare, but keep the current value visible if so).
    const stadeOptionsForMatch = useCallback((m: IMatch) => {
        const opts = [...baseStadeOptions];
        const cur = m.Stade_Idt ? Number(m.Stade_Idt) : null;
        if (cur && !opts.some(o => o.value === cur)) {
            const label = m.Stade_Nom ? `${m.Stade_Nom} (${m.Ville_Nom ?? '—'})` : `#${cur}`;
            opts.push({value: cur, label});
        }
        return opts;
    }, [baseStadeOptions]);

    const sortedMatches = useMemo(() => {
        return [...matches].sort((a, b) => {
            const ta = matchAsUtcDate(a.Dte, a.Hre)?.getTime() ?? Number.POSITIVE_INFINITY;
            const tb = matchAsUtcDate(b.Dte, b.Hre)?.getTime() ?? Number.POSITIVE_INFINITY;
            return ta - tb || (a.Num ?? 0) - (b.Num ?? 0);
        });
    }, [matches]);

    const performAutoSave = useCallback(async (matchIdt: number) => {
        const match = matchesRef.current.find(m => m.Idt === matchIdt);
        const draft = draftsRef.current[matchIdt];
        if (!match || !draft) return;
        const baseline = buildDraft(match);
        if (sameDraft(draft, baseline)) return;
        if (draft.teamIdtDom && draft.teamIdtExt && draft.teamIdtDom === draft.teamIdtExt) {
            message.error('Domicile et extérieur doivent être deux équipes différentes.');
            return;
        }
        const metaPayload: {num?: number; hre?: string; dte?: string} = {};
        if (draft.num !== null && draft.num !== baseline.num) metaPayload.num = draft.num;
        if (draft.hre && draft.hre.format('HH:mm:ss') !== baseline.hre?.format('HH:mm:ss')) {
            metaPayload.hre = draft.hre.format('HH:mm:ss');
        }
        if (draft.dte && draft.dte.format('YYYY-MM-DD') !== baseline.dte?.format('YYYY-MM-DD')) {
            metaPayload.dte = draft.dte.format('YYYY-MM-DD');
        }
        const teamsPayload: {teamIdtDom?: number; teamIdtExt?: number} = {};
        if (draft.teamIdtDom !== null && draft.teamIdtDom !== baseline.teamIdtDom) {
            teamsPayload.teamIdtDom = draft.teamIdtDom;
        }
        if (draft.teamIdtExt !== null && draft.teamIdtExt !== baseline.teamIdtExt) {
            teamsPayload.teamIdtExt = draft.teamIdtExt;
        }
        const thirdPlaceChanged = draft.isThirdPlace !== baseline.isThirdPlace;
        const stadeChanged = draft.stadeIdt !== null && draft.stadeIdt !== baseline.stadeIdt;
        if (Object.keys(metaPayload).length === 0
            && Object.keys(teamsPayload).length === 0
            && !thirdPlaceChanged
            && !stadeChanged) {
            return;
        }
        setSavingIdts(prev => {
            const next = new Set(prev);
            next.add(matchIdt);
            return next;
        });
        try {
            if (Object.keys(metaPayload).length > 0) {
                await AxiosServices.adminUpdateMatchMeta(matchIdt, metaPayload);
            }
            if (Object.keys(teamsPayload).length > 0) {
                await AxiosServices.adminUpdateMatchTeams(matchIdt, teamsPayload);
            }
            if (thirdPlaceChanged) {
                // Toggle is sent on its own — the backend auto-fills
                // both L slots when turning on, and clears them off.
                await AxiosServices.adminUpdateMatchThirdPlace(matchIdt, draft.isThirdPlace);
            }
            if (stadeChanged && draft.stadeIdt) {
                await AxiosServices.adminUpdateMatchStade(matchIdt, draft.stadeIdt);
            }
            message.success(`Match n°${match.Num} enregistré.`);
            await refresh();
        } catch (err: any) {
            const status = err?.response?.status;
            const serverMsg = err?.response?.data?.error ?? err?.response?.data ?? err?.message;
            message.error(`Échec ${status ?? ''} : ${serverMsg ?? 'erreur inconnue'}`);
        } finally {
            setSavingIdts(prev => {
                const next = new Set(prev);
                next.delete(matchIdt);
                return next;
            });
        }
    }, [message, refresh]);

    // 600ms keeps InputNumber typing fluid while still feeling
    // immediate for Select/DatePicker/TimePicker pickers.
    const scheduleAutoSave = useCallback((matchIdt: number) => {
        const existing = saveTimers.current.get(matchIdt);
        if (existing) clearTimeout(existing);
        const t = setTimeout(() => {
            saveTimers.current.delete(matchIdt);
            performAutoSave(matchIdt);
        }, AUTOSAVE_DELAY_MS);
        saveTimers.current.set(matchIdt, t);
    }, [performAutoSave]);

    // Flush any pending timers on unmount so a navigation away
    // doesn't drop the last edit silently.
    useEffect(() => {
        const timers = saveTimers.current;
        return () => {
            timers.forEach((t, idt) => {
                clearTimeout(t);
                performAutoSave(idt);
            });
            timers.clear();
        };
    }, [performAutoSave]);

    if (!isLoggedIn) {
        return (
            <div style={{padding: 24}}>
                <Empty description="Connecte-toi pour accéder à cette page."/>
            </div>
        );
    }

    if (!isAdmin) {
        return (
            <div style={{padding: 24}}>
                <Alert
                    type="error"
                    showIcon
                    icon={<LockOutlined/>}
                    message="Accès refusé"
                    description="Cette page est réservée aux administrateurs."
                />
            </div>
        );
    }

    if (!currentCmpEdt) {
        return (
            <div style={{padding: 24}}>
                <Empty description="Sélectionne d'abord une compétition dans la sidebar."/>
            </div>
        );
    }

    const updateDraft = (idt: number, patch: Partial<RowDraft>) => {
        setDrafts(prev => ({...prev, [idt]: {...prev[idt], ...patch}}));
        scheduleAutoSave(idt);
    };

    const columns = [
        {
            title: 'N°',
            key: 'Num',
            width: 90,
            render: (_: any, m: IMatch) => (
                <InputNumber
                    min={1}
                    max={9999}
                    value={drafts[m.Idt]?.num ?? null}
                    onChange={(v) => updateDraft(m.Idt, {num: v as number | null})}
                    style={{width: 80}}
                />
            ),
        },
        {
            title: 'Date',
            key: 'date',
            width: 160,
            render: (_: any, m: IMatch) => (
                <DatePicker
                    value={drafts[m.Idt]?.dte ?? null}
                    onChange={(v) => updateDraft(m.Idt, {dte: v})}
                    format="DD/MM/YYYY"
                    style={{width: 140}}
                    allowClear={false}
                />
            ),
        },
        {
            title: 'Heure',
            key: 'hre',
            width: 130,
            render: (_: any, m: IMatch) => (
                <TimePicker
                    value={drafts[m.Idt]?.hre ?? null}
                    onChange={(v) => updateDraft(m.Idt, {hre: v})}
                    format="HH:mm"
                    minuteStep={5}
                    style={{width: 110}}
                    allowClear={false}
                />
            ),
        },
        {
            title: '3e place',
            key: 'isThirdPlace',
            width: 70,
            align: 'center' as const,
            render: (_: any, m: IMatch) => {
                if ((m.Phs_Cod ?? 'G').trim().toUpperCase() === 'G') return null;
                return (
                    <Checkbox
                        checked={drafts[m.Idt]?.isThirdPlace === true}
                        onChange={(e) => updateDraft(m.Idt, {isThirdPlace: e.target.checked})}
                    />
                );
            },
        },
        {
            title: 'Domicile',
            key: 'Nom_Dom',
            align: 'right' as const,
            render: (_: any, m: IMatch) => {
                const isTP = drafts[m.Idt]?.isThirdPlace === true;
                return (
                    <Select
                        placeholder="Équipe domicile"
                        value={drafts[m.Idt]?.teamIdtDom ?? undefined}
                        onChange={(v) => updateDraft(m.Idt, {teamIdtDom: (v as number | undefined) ?? null})}
                        options={teamOptionsForSide(m, 'D')}
                        showSearch
                        optionFilterProp="label"
                        allowClear
                        disabled={isTP}
                        style={{minWidth: 200}}
                        size="small"
                    />
                );
            },
        },
        {
            title: 'Extérieur',
            key: 'Nom_Ext',
            render: (_: any, m: IMatch) => {
                const isTP = drafts[m.Idt]?.isThirdPlace === true;
                return (
                    <Select
                        placeholder="Équipe extérieur"
                        value={drafts[m.Idt]?.teamIdtExt ?? undefined}
                        onChange={(v) => updateDraft(m.Idt, {teamIdtExt: (v as number | undefined) ?? null})}
                        options={teamOptionsForSide(m, 'E')}
                        showSearch
                        optionFilterProp="label"
                        allowClear
                        disabled={isTP}
                        style={{minWidth: 200}}
                        size="small"
                    />
                );
            },
        },
        {
            title: 'Stade',
            key: 'Stade_Nom',
            render: (_: any, m: IMatch) => (
                <Select
                    placeholder="Stade"
                    value={drafts[m.Idt]?.stadeIdt ?? undefined}
                    onChange={(v) => updateDraft(m.Idt, {stadeIdt: (v as number | undefined) ?? null})}
                    options={stadeOptionsForMatch(m)}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    style={{minWidth: 220}}
                    size="small"
                />
            ),
        },
        {
            title: '',
            key: 'status',
            width: 40,
            align: 'center' as const,
            render: (_: any, m: IMatch) => (
                savingIdts.has(m.Idt) ? <Spin size="small"/> : null
            ),
        },
    ];

    return (
        <div style={{padding: 16, height: '100%', boxSizing: 'border-box', overflow: 'hidden', background: ACCENT}}>
            <Card
                styles={{
                    header: {background: ACCENT, color: '#fff'},
                    body: {background: ACCENT, color: '#fff'},
                }}
                style={{background: ACCENT, borderColor: ACCENT}}
                title={
                    <Space>
                        <TeamOutlined/>
                        <span>Administration — Matches</span>
                    </Space>
                }
            >
                <Paragraph style={{marginBottom: 16, color: '#cfd8d3'}}>
                    Modifie le numéro, la date, l'heure, les équipes et le stade :
                    la sauvegarde est automatique dès qu'un champ change.
                    Pour les phases finales, l'équipe proposée est le vainqueur du match
                    précédent une fois son résultat connu. Pour le match de classement
                    3<sup>e</sup>/4<sup>e</sup> place, cocher la case dédiée : les deux
                    côtés sont alors auto-remplis avec les perdants des demi-finales.
                </Paragraph>
                <ConfigProvider theme={{algorithm: theme.darkAlgorithm}}>
                    <Table
                        dataSource={sortedMatches}
                        columns={columns}
                        rowKey={(m) => m.Idt.toString()}
                        loading={loading}
                        pagination={false}
                        size="small"
                        sticky
                        scroll={{y: 'calc(100vh - 360px)'}}
                    />
                </ConfigProvider>
            </Card>
        </div>
    );
};

export default AdminMatches;
